import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Spin,
  message,
  Typography,
  Button,
  Select,
  DatePicker,
  Space,
  Tag,
  Alert,
} from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  WarningOutlined,
  ShopOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Parsear timestamp de backend (hora local Argentina)
const formatFechaConHora = (timestamp) => {
  if (!timestamp) return '-';
  if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
    return dayjs(timestamp.replace(' ', 'T')).format('DD/MM/YYYY HH:mm');
  }
  return dayjs(timestamp).format('DD/MM/YYYY HH:mm');
};

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const COLORS = ['#52c41a', '#1890ff', '#faad14', '#eb2f96', '#13c2c2', '#722ed1', '#f5222d', '#2f54eb'];

const DashboardGestion = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [periodo, setPeriodo] = useState('hoy');
  const [fechaDesde, setFechaDesde] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [periodo, fechaDesde, fechaHasta]);

  const getFechasPeriodo = () => {
    const hoy = dayjs();
    let desde, hasta;

    switch (periodo) {
      case 'hoy':
        desde = hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'ayer':
        desde = hasta = hoy.subtract(1, 'day').format('YYYY-MM-DD');
        break;
      case 'ultimos7':
        desde = hoy.subtract(7, 'day').format('YYYY-MM-DD');
        hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'ultimos30':
        desde = hoy.subtract(30, 'day').format('YYYY-MM-DD');
        hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'mesActual':
        desde = hoy.startOf('month').format('YYYY-MM-DD');
        hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'mesAnterior':
        desde = hoy.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        hasta = hoy.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      case 'anoActual':
        desde = hoy.startOf('year').format('YYYY-MM-DD');
        hasta = hoy.format('YYYY-MM-DD');
        break;
      case 'personalizado':
        desde = fechaDesde ? fechaDesde.format('YYYY-MM-DD') : null;
        hasta = fechaHasta ? fechaHasta.format('YYYY-MM-DD') : null;
        break;
      default:
        desde = hasta = hoy.format('YYYY-MM-DD');
    }

    return { desde, hasta };
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const { desde, hasta } = getFechasPeriodo();
      
      const params = new URLSearchParams();
      if (desde) params.append('fecha_desde', desde);
      if (hasta) params.append('fecha_hasta', hasta);
      
      const response = await apiClient.get(`/gestion/dashboard?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los datos del dashboard');
      }
    } catch (error) {
      console.error('Error al cargar dashboard:', error);
      message.error('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatMoneda = (n) =>
    `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const formatFecha = (dateString) => (dateString ? dayjs(dateString).format('DD/MM/YYYY') : '-');
  const formatFechaCorta = (dateString) => (dateString ? dayjs(dateString).format('DD/MM') : '-');

  // Datos para gráfico de ingresos vs egresos
  const ingresosVsEgresosData = useMemo(() => {
    if (!dashboardData?.graficos?.ingresosVsEgresos) return [];
    return dashboardData.graficos.ingresosVsEgresos.map(item => ({
      fecha: formatFechaCorta(item.fecha),
      ingresos: item.ingresos,
      egresos: item.egresos,
      ganancia: item.ganancia,
    }));
  }, [dashboardData?.graficos?.ingresosVsEgresos]);

  // Datos para gráfico de ventas por categoría
  const ventasPorCategoriaData = useMemo(() => {
    if (!dashboardData?.graficos?.ventasPorCategoria) return [];
    return dashboardData.graficos.ventasPorCategoria.map(item => ({
      categoria: item.categoria,
      monto: item.monto_total,
      cantidad: item.cantidad_vendida,
    }));
  }, [dashboardData?.graficos?.ventasPorCategoria]);

  // Datos para gráfico de ingresos por forma de pago
  const ingresosPorFormaPagoData = useMemo(() => {
    if (!dashboardData?.graficos?.ingresosPorFormaPago) return [];
    return dashboardData.graficos.ingresosPorFormaPago.map((item, index) => ({
      name: item.forma_pago,
      value: item.monto_total,
      cantidad: item.cantidad_ventas,
      fill: COLORS[index % COLORS.length],
    }));
  }, [dashboardData?.graficos?.ingresosPorFormaPago]);

  // Datos para gráfico de gastos por artículo
  const gastosPorPeriodoData = useMemo(() => {
    if (!dashboardData?.graficos?.gastosPorPeriodo) return [];
    return dashboardData.graficos.gastosPorPeriodo.map((item) => ({
      nombre: item.articulo_nombre,
      total: item.total,
      cantidad: item.cantidad,
    }));
  }, [dashboardData?.graficos?.gastosPorPeriodo]);

  // Columnas para tabla de top productos
  const columnsTopProductos = [
    {
      title: 'Producto',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Cantidad Vendida',
      dataIndex: 'cantidad_vendida',
      key: 'cantidad_vendida',
      align: 'center',
    },
    {
      title: 'Monto Total',
      dataIndex: 'monto_total',
      key: 'monto_total',
      align: 'right',
      render: (monto) => formatMoneda(monto),
    },
  ];

  // Columnas para tabla de top proveedores
  const columnsTopProveedores = [
    {
      title: 'Proveedor',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Cantidad Compras',
      dataIndex: 'cantidad_compras',
      key: 'cantidad_compras',
      align: 'center',
    },
    {
      title: 'Monto Total',
      dataIndex: 'monto_total',
      key: 'monto_total',
      align: 'right',
      render: (monto) => formatMoneda(monto),
    },
  ];

  // Columnas para tabla de stock bajo
  const columnsStockBajo = [
    {
      title: 'Artículo',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Stock Actual',
      dataIndex: 'stock_actual',
      key: 'stock_actual',
      align: 'center',
      render: (stock) => <Tag color="red">{stock}</Tag>,
    },
    {
      title: 'Stock Mínimo',
      dataIndex: 'stock_minimo',
      key: 'stock_minimo',
      align: 'center',
    },
    {
      title: 'Diferencia',
      dataIndex: 'diferencia',
      key: 'diferencia',
      align: 'center',
      render: (diff) => <Tag color="orange">-{diff}</Tag>,
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria',
      key: 'categoria',
    },
  ];

  // Columnas para últimas ventas
  const columnsUltimasVentas = [
    {
      title: 'Fecha',
      dataIndex: 'fecha_creacion',
      key: 'fecha',
      render: (fechaCreacion, record) => fechaCreacion ? formatFechaConHora(fechaCreacion) : formatFecha(record.fecha),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (total) => formatMoneda(total),
    },
    {
      title: 'Método Pago',
      dataIndex: 'metodo_pago',
      key: 'metodo_pago',
    },
    {
      title: 'Items',
      dataIndex: 'cantidad_items',
      key: 'cantidad_items',
      align: 'center',
    },
  ];

  // Columnas para últimas compras
  const columnsUltimasCompras = [
    {
      title: 'Fecha',
      dataIndex: 'fecha_creacion',
      key: 'fecha',
      render: (fechaCreacion, record) => fechaCreacion ? formatFechaConHora(fechaCreacion) : formatFecha(record.fecha),
    },
    {
      title: 'Proveedor',
      dataIndex: 'proveedor_nombre',
      key: 'proveedor_nombre',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (total) => total ? formatMoneda(total) : '-',
    },
    {
      title: 'Items',
      dataIndex: 'cantidad_items',
      key: 'cantidad_items',
      align: 'center',
    },
  ];

  if (loading && !dashboardData) {
    return (
      <MainLayout>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  const stats = dashboardData?.stats || {};
  const gananciaNeta = stats.ganancia?.neta || 0;
  const margenGanancia = stats.ganancia?.margen || 0;

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ color: '#ffffff', marginBottom: 16 }}>
            Dashboard de Gestión
          </Title>
          
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              value={periodo}
              onChange={(value) => {
                setPeriodo(value);
                if (value !== 'personalizado') {
                  setFechaDesde(null);
                  setFechaHasta(null);
                }
              }}
              style={{ width: 200 }}
            >
              <Select.Option value="hoy">Hoy</Select.Option>
              <Select.Option value="ayer">Ayer</Select.Option>
              <Select.Option value="ultimos7">Últimos 7 días</Select.Option>
              <Select.Option value="ultimos30">Últimos 30 días</Select.Option>
              <Select.Option value="mesActual">Mes actual</Select.Option>
              <Select.Option value="mesAnterior">Mes anterior</Select.Option>
              <Select.Option value="anoActual">Año actual</Select.Option>
              <Select.Option value="personalizado">Rango personalizado</Select.Option>
            </Select>
            
            {periodo === 'personalizado' && (
              <RangePicker
                value={fechaDesde && fechaHasta ? [fechaDesde, fechaHasta] : null}
                onChange={(dates) => {
                  if (dates) {
                    setFechaDesde(dates[0]);
                    setFechaHasta(dates[1]);
                  } else {
                    setFechaDesde(null);
                    setFechaHasta(null);
                  }
                }}
                format="DD/MM/YYYY"
              />
            )}
            
            <Button
              icon={<ReloadOutlined />}
              onClick={loadDashboardData}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
        </div>

        {/* Alertas */}
        {dashboardData?.alertas?.stockBajo?.length > 0 && (
          <Alert
            message={`${dashboardData.alertas.stockBajo.length} artículo(s) con stock bajo`}
            type="warning"
            icon={<WarningOutlined />}
            style={{ marginBottom: 24 }}
            showIcon
          />
        )}

        {/* KPIs principales */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Ingresos"
                value={stats.caja?.ingresos ?? stats.ingresos?.total ?? 0}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#52c41a' }}
                formatter={(value) => formatMoneda(value)}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {stats.ingresos?.cantidad || 0} ventas • Coincide con Caja Diaria
              </Text>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Egresos"
                value={stats.egresos?.total || 0}
                prefix={<ShoppingCartOutlined />}
                valueStyle={{ color: '#f5222d' }}
                formatter={(value) => formatMoneda(value)}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Compras: {formatMoneda(stats.egresos?.compras || 0)} • Gastos: {formatMoneda(stats.egresos?.gastos || 0)}
              </Text>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Ganancia Neta"
                value={gananciaNeta}
                prefix={gananciaNeta >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                valueStyle={{ color: gananciaNeta >= 0 ? '#52c41a' : '#f5222d' }}
                formatter={(value) => formatMoneda(value)}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Margen: {margenGanancia.toFixed(2)}%
              </Text>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Saldo Caja"
                value={stats.caja?.saldo || 0}
                prefix={<DollarOutlined />}
                valueStyle={{ color: stats.caja?.saldo >= 0 ? '#52c41a' : '#f5222d' }}
                formatter={(value) => formatMoneda(value)}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Ingresos: {formatMoneda(stats.caja?.ingresos || 0)} • Egresos: {formatMoneda(stats.caja?.egresos || 0)}
              </Text>
            </Card>
          </Col>
        </Row>

        {/* KPIs secundarios */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Valor Inventario"
                value={stats.stock?.valorTotal || 0}
                prefix={<DatabaseOutlined />}
                valueStyle={{ color: '#1890ff' }}
                formatter={(value) => formatMoneda(value)}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Stock Bajo"
                value={stats.stock?.stockBajoCount || 0}
                prefix={<WarningOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Proveedores Activos"
                value={stats.proveedores?.activos || 0}
                prefix={<ShopOutlined />}
                valueStyle={{ color: '#13c2c2' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Gráficos principales */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Ingresos vs Egresos"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={ingresosVsEgresosData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                  <XAxis dataKey="fecha" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} />
                  <YAxis stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                    labelStyle={{ color: '#e8e8e8' }}
                    formatter={(value) => formatMoneda(value)}
                  />
                  <Legend wrapperStyle={{ color: '#b0b0b0' }} />
                  <Area type="monotone" dataKey="ingresos" stackId="1" stroke="#52c41a" fill="#52c41a" fillOpacity={0.6} />
                  <Area type="monotone" dataKey="egresos" stackId="2" stroke="#f5222d" fill="#f5222d" fillOpacity={0.6} />
                  <Line type="monotone" dataKey="ganancia" stroke="#1890ff" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <Card
              title="Ingresos por Forma de Pago"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={ingresosPorFormaPagoData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {ingresosPorFormaPagoData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                    labelStyle={{ color: '#e8e8e8' }}
                    formatter={(value) => formatMoneda(value)}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Gráfico de gastos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Gastos por artículo"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              {gastosPorPeriodoData.length === 0 ? (
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>
                  No hay gastos en el período seleccionado
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(300, gastosPorPeriodoData.length * 32)}>
                  <BarChart data={gastosPorPeriodoData} layout="vertical" margin={{ left: 8, right: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                    <XAxis type="number" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                    <YAxis dataKey="nombre" type="category" width={180} stroke="#8c8c8c" tick={{ fill: '#b0b0b0', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                      labelStyle={{ color: '#e8e8e8' }}
                      formatter={(value) => formatMoneda(value)}
                      labelFormatter={(label, payload) => (payload?.[0]?.payload ? `${payload[0].payload.nombre} (cant: ${payload[0].payload.cantidad})` : label)}
                    />
                    <Bar dataKey="total" name="Gastos" fill="#f5222d" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
        </Row>

        {/* Gráficos de barras */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Top 10 Productos Más Vendidos"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData?.top?.productos?.slice(0, 10) || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                  <XAxis type="number" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                  <YAxis dataKey="nombre" type="category" width={150} stroke="#8c8c8c" tick={{ fill: '#b0b0b0', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                    labelStyle={{ color: '#e8e8e8' }}
                    formatter={(value) => formatMoneda(value)}
                  />
                  <Bar dataKey="monto_total" fill="#52c41a" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <Card
              title="Ventas por Categoría"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ventasPorCategoriaData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                  <XAxis dataKey="categoria" stroke="#8c8c8c" tick={{ fill: '#b0b0b0', fontSize: 12 }} angle={-45} textAnchor="end" height={100} />
                  <YAxis stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                    labelStyle={{ color: '#e8e8e8' }}
                    formatter={(value) => formatMoneda(value)}
                  />
                  <Bar dataKey="monto" fill="#1890ff" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Tablas */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Top 10 Productos"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <Table
                dataSource={dashboardData?.top?.productos || []}
                columns={columnsTopProductos}
                pagination={false}
                size="small"
                rowKey="articulo_id"
              />
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <Card
              title="Top 10 Proveedores"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <Table
                dataSource={dashboardData?.top?.proveedores || []}
                columns={columnsTopProveedores}
                pagination={false}
                size="small"
                rowKey="proveedor_id"
              />
            </Card>
          </Col>
        </Row>

        {/* Alertas y recientes */}
        <Row gutter={[16, 16]}>
          {dashboardData?.alertas?.stockBajo?.length > 0 && (
            <Col xs={24} lg={12}>
              <Card
                title={<><WarningOutlined style={{ color: '#faad14' }} /> Artículos con Stock Bajo</>}
                style={{ background: '#2d2d2d', borderColor: '#404040' }}
              >
                <Table
                  dataSource={dashboardData.alertas.stockBajo}
                  columns={columnsStockBajo}
                  pagination={false}
                  size="small"
                  rowKey="articulo_id"
                />
              </Card>
            </Col>
          )}
          
          <Col xs={24} lg={dashboardData?.alertas?.stockBajo?.length > 0 ? 12 : 24}>
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Card
                  title="Últimas Ventas"
                  style={{ background: '#2d2d2d', borderColor: '#404040' }}
                >
                  <Table
                    dataSource={dashboardData?.recientes?.ventas || []}
                    columns={columnsUltimasVentas}
                    pagination={false}
                    size="small"
                    rowKey="venta_id"
                  />
                </Card>
              </Col>
              
              <Col xs={24}>
                <Card
                  title="Últimas Compras"
                  style={{ background: '#2d2d2d', borderColor: '#404040' }}
                >
                  <Table
                    dataSource={dashboardData?.recientes?.compras || []}
                    columns={columnsUltimasCompras}
                    pagination={false}
                    size="small"
                    rowKey="compra_id"
                  />
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </MainLayout>
  );
};

export default DashboardGestion;
